import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Database, Trash2, AlertTriangle, ImageIcon, Bot, User, RefreshCw, HardDrive } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const AdminDatabaseManagement = () => {
  const [stats, setStats] = useState({
    totalImages: 0,
    automationImages: 0,
    userImages: 0,
    estimatedSizeMB: 0
  });
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [finalConfirm, setFinalConfirm] = useState(false);

  const CONFIRM_PHRASE = 'DELETE ALL IMAGES';

  const fetchStats = async () => {
    setLoading(true);
    try {
      // Use admin RPC function to bypass RLS
      const { data, error } = await supabase.rpc('get_admin_database_stats');

      if (error) {
        console.error('Error fetching stats via RPC:', error);
        throw error;
      }

      if (data) {
        const statsData = data as { totalImages: number; automationImages: number; userImages: number; estimatedSizeMB: number };
        setStats({
          totalImages: statsData.totalImages || 0,
          automationImages: statsData.automationImages || 0,
          userImages: statsData.userImages || 0,
          estimatedSizeMB: statsData.estimatedSizeMB || 0
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to fetch database stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const flushAllImages = async () => {
    if (confirmPhrase !== CONFIRM_PHRASE || !finalConfirm) {
      toast.error('Please complete all confirmation steps');
      return;
    }

    setDeleting(true);
    setProgress(0);
    setDeletedCount(0);
    setShowConfirmDialog(false);

    const toastId = toast.loading('Starting deletion...');

    try {
      const totalToDelete = stats.totalImages;
      let deleted = 0;
      const BATCH_SIZE = 100;

      while (true) {
        // Use admin RPC function to delete in batches
        const { data, error } = await supabase.rpc('admin_delete_images_batch', { 
          batch_size: BATCH_SIZE 
        });

        if (error) {
          throw error;
        }

        const result = data as { deleted: number; remaining: number };
        
        if (result.deleted === 0) {
          break;
        }

        deleted += result.deleted;
        setDeletedCount(deleted);
        const progressPercent = Math.min((deleted / totalToDelete) * 100, 100);
        setProgress(progressPercent);

        toast.loading(`Deleting images: ${deleted.toLocaleString()} / ${totalToDelete.toLocaleString()}`, { id: toastId });

        // Small delay to prevent overwhelming the database
        await new Promise(r => setTimeout(r, 100));
      }

      toast.success(`Successfully deleted ${deleted.toLocaleString()} images`, { id: toastId });
      
      // Reset form and refresh stats
      setConfirmPhrase('');
      setFinalConfirm(false);
      await fetchStats();
    } catch (error: any) {
      console.error('Error flushing images:', error);
      toast.error(`Failed to delete images: ${error.message}`, { id: toastId });
    } finally {
      setDeleting(false);
      setProgress(0);
    }
  };

  const formatSize = (mb: number) => {
    if (mb >= 1000) {
      return `${(mb / 1000).toFixed(1)} GB`;
    }
    return `${mb.toFixed(0)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Storage Overview */}
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="w-5 h-5 text-blue-400" />
              <div>
                <CardTitle className="text-white">Database Storage</CardTitle>
                <CardDescription className="text-gray-400">Generated images storage overview</CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchStats}
              disabled={loading}
              className="text-gray-400 hover:text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <ImageIcon className="w-4 h-4 text-purple-400" />
                <span className="text-sm text-gray-400">Total Images</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {loading ? '...' : stats.totalImages.toLocaleString()}
              </p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <HardDrive className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-gray-400">Est. Storage</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {loading ? '...' : formatSize(stats.estimatedSizeMB)}
              </p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Bot className="w-4 h-4 text-green-400" />
                <span className="text-sm text-gray-400">From Automation</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {loading ? '...' : stats.automationImages.toLocaleString()}
              </p>
            </div>
            
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-gray-400">User Generated</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {loading ? '...' : stats.userImages.toLocaleString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="bg-red-950/20 backdrop-blur-md border-red-500/30">
        <CardHeader>
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div>
              <CardTitle className="text-red-400">Danger Zone</CardTitle>
              <CardDescription className="text-red-300/70">Irreversible actions that affect all data</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-red-950/30 rounded-lg p-4 border border-red-500/20">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-white font-medium flex items-center gap-2">
                  <Trash2 className="w-4 h-4 text-red-400" />
                  Flush All Images
                </h4>
                <p className="text-sm text-gray-400 mt-1">
                  Permanently delete all {stats.totalImages.toLocaleString()} images from the database. 
                  This will free up approximately {formatSize(stats.estimatedSizeMB)} of storage.
                </p>
              </div>
              
              <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    disabled={deleting || stats.totalImages === 0}
                    className="bg-red-600 hover:bg-red-700 shrink-0"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete All Images
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-slate-900 border-red-500/30">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-red-400 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Delete All Generated Images?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-gray-300 space-y-4">
                      <p>
                        You are about to permanently delete <strong className="text-white">{stats.totalImages.toLocaleString()} images</strong> ({formatSize(stats.estimatedSizeMB)}).
                      </p>
                      
                      <div className="bg-red-950/50 rounded-lg p-3 border border-red-500/30">
                        <p className="text-red-300 text-sm">
                          ⚠️ This action cannot be undone. All image data will be permanently lost.
                        </p>
                      </div>

                      <div className="space-y-3">
                        <label className="text-sm text-gray-400">
                          Type <span className="font-mono bg-white/10 px-2 py-0.5 rounded text-white">DELETE ALL IMAGES</span> to confirm:
                        </label>
                        <Input
                          value={confirmPhrase}
                          onChange={(e) => setConfirmPhrase(e.target.value)}
                          placeholder="Type confirmation phrase..."
                          className="bg-white/5 border-white/20 text-white"
                        />
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="final-confirm"
                          checked={finalConfirm}
                          onCheckedChange={(checked) => setFinalConfirm(checked === true)}
                          className="border-red-500/50 data-[state=checked]:bg-red-600"
                        />
                        <label htmlFor="final-confirm" className="text-sm text-gray-300 cursor-pointer">
                          I understand this action is permanent and cannot be reversed
                        </label>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-white/10 text-white border-white/20 hover:bg-white/20">
                      Cancel
                    </AlertDialogCancel>
                    <Button
                      onClick={flushAllImages}
                      disabled={confirmPhrase !== CONFIRM_PHRASE || !finalConfirm}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete All Images
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            {/* Progress indicator when deleting */}
            {deleting && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Deleting images...</span>
                  <span className="text-white">{deletedCount.toLocaleString()} / {stats.totalImages.toLocaleString()}</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDatabaseManagement;
